/**
 * The ONLY place this app talks to the network.
 *
 * Two invariants it exists to hold:
 *   • every authenticated call carries the opaque guest token in `x-guest-session-token` — and nothing else.
 *     There is no Authorization header, no cookie, no staff JWT. A guest credential cannot reach /api/* by
 *     construction (the server builds req.user with no _id and no role, so canPerform can never pass), and
 *     this client must not be the thing that tries.
 *   • every failure becomes a { code, message } a screen can render, with `message` already human-safe.
 */
import { getSession } from '../session'
import { humanMessage } from './humanError'
import type { GuestError } from './contract'

/** Set at build time. Empty in dev, where vite proxies /guest to the local backend. */
const BASE = (import.meta as any).env?.VITE_API_BASE ?? ''

/**
 * A submission's replay key. The server's whole at-most-once story hangs on the client supplying one
 * (`body.idempotencyKey`); with none it mints a fresh id per request, every derived key is unique, and the
 * replay branch is dead. `randomUUID` is unavailable on http:// origins and in older Safari, so fall back
 * rather than throw — a weaker key still dedupes a retry of the SAME submission, which is the entire job.
 */
export function newIdempotencyKey(): string {
  const c: any = (globalThis as any).crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  if (c && typeof c.getRandomValues === 'function') {
    const b = c.getRandomValues(new Uint8Array(16))
    return Array.from(b as Uint8Array, (n: number) => n.toString(16).padStart(2, '0')).join('')
  }
  return `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`
}

export class GuestApiError extends Error {
  readonly code: string
  readonly status: number
  readonly retryAfterSec?: number
  constructor(code: string, message: string, status: number, retryAfterSec?: number) {
    super(message)
    this.name = 'GuestApiError'
    this.code = code
    this.status = status
    this.retryAfterSec = retryAfterSec
  }
}

type Method = 'GET' | 'POST'

async function call<T>(method: Method, path: string, body?: unknown, opts?: { auth?: boolean }): Promise<T> {
  const auth = opts?.auth !== false
  const headers: Record<string, string> = { accept: 'application/json' }
  if (body !== undefined) headers['content-type'] = 'application/json'
  if (auth) {
    const token = getSession()
    // Fail here rather than sending an unauthenticated request the server will refuse with `no_session`:
    // the screen can route to "open your invitation link again" without a round trip.
    if (!token) throw new GuestApiError('no_session', humanMessage('', 'no_session'), 0)
    headers['x-guest-session-token'] = token
  }

  let res: Response
  try {
    res = await fetch(`${BASE}/guest${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new GuestApiError('network_error', humanMessage('', 'network_error'), 0)
  }

  const text = await res.text()
  let parsed: unknown = undefined
  let unparseable = false
  if (text) { try { parsed = JSON.parse(text) } catch { unparseable = true } }

  // SOMETHING THAT IS NOT THE API ANSWERED. Every /guest endpoint returns JSON or an empty body, so a
  // non-empty body that will not parse means the response came from a host that is not the API — which is
  // exactly what a misbuilt bundle produces: with VITE_API_BASE empty the calls go to the portal's OWN
  // origin and the static host replies with an HTML page. That shipped (see netlify.toml).
  //
  // It is caught HERE, on the body, rather than by asserting something about BASE, because the base being
  // empty is legitimate in dev (vite proxies /guest) and would also be legitimate behind a reverse proxy
  // that serves the API and the portal on one origin. The body cannot lie about who answered; the config
  // cannot tell us the topology. Checked on BOTH branches: a static host that answers 200 with its shell
  // is the more dangerous case, since the old code returned `undefined` as the payload and the screen
  // crashed reaching into it.
  if (unparseable) throw new GuestApiError('invalid_response', humanMessage('', 'invalid_response'), res.status)

  if (!res.ok) {
    const err = (parsed ?? {}) as Partial<GuestError>
    const code = err.code || `http_${res.status}`
    throw new GuestApiError(code, humanMessage(err.message, err.code), res.status, err.retryAfterSec)
  }
  return parsed as T
}

/**
 * Pull the list out of a server envelope — and REFUSE if it is not there.
 *
 * 🔴 THE DEFECT THIS EXISTS FOR SHIPPED AND KILLED THE PRODUCT. The guest list endpoints do NOT share one
 * envelope: `/issue-desks` returns `{desks}`, `/issues` returns `{issues,…}`, `/issues/:id/comments` returns
 * `{comments,…}` — while `/workspaces` returns `{items,count}`. The app was written reading `.items`
 * everywhere, generalised from the one endpoint that uses it. All three core lists came back `undefined`, and
 * every screen fell to `?? []` and rendered its EMPTY state. A guest with a live desk and a live grant was
 * told "There is nothing set up for you to report against yet." The whole point of the portal was dead, on a
 * green build with a green suite.
 *
 * So the missing-key case must NOT be `?? []`. An empty list and a broken contract are different facts, and
 * making them render identically is precisely what hid this for an entire release. A rename on the server now
 * produces a visible failure the customer can report, not a silent lie that everything is fine.
 */
function listOf<T>(payload: any, key: string): T[] {
  const v = payload == null ? undefined : payload[key]
  if (!Array.isArray(v)) throw new GuestApiError('invalid_response', humanMessage('', 'invalid_response'), 200)
  return v as T[]
}

export const api = {
  // ── pre-session ──
  invitation: (token: string) => call<import('./contract').InvitationView>('GET', `/invitations/${encodeURIComponent(token)}`, undefined, { auth: false }),
  accept: (token: string) => call<import('./contract').AcceptedSession>('POST', `/invitations/${encodeURIComponent(token)}/accept`, undefined, { auth: false }),

  // ── session ──
  // Each list returns the ARRAY, never the envelope: the transport shape stops here, so no screen can read
  // the wrong key again. Envelope keys below are the ones the LIVE API actually sends (captured 2026-07-30
  // against portal.autoryn.ai → the deployed backend), not the ones this app assumed.
  desks: async (): Promise<import('./contract').Desk[]> =>
    listOf<import('./contract').Desk>(await call<any>('GET', '/issue-desks'), 'desks'),
  desk: (deskId: string) => call<import('./contract').Desk>('GET', `/issue-desks/${deskId}`),
  submitIssue: (deskId: string, payload: Record<string, unknown>) =>
    call<import('./contract').Issue>('POST', `/issue-desks/${deskId}/issues`, payload),

  /**
   * ALL the guest's issues, not the first page.
   *
   * 🔴 The server pages at 25 (`limit` clamped by MAX_PAGE_SIZE) and hands back `hasMore` + `nextBefore`; the
   * portal read `issues` and dropped both, so a guest with more than 25 live issues simply could not see the
   * older ones — no control, no message, no indication anything was missing. Latent rather than currently
   * biting (stock desks allow 10 issues/day), but silent truncation of a customer's own history is the same
   * failure mode as the empty-list bug: absence rendered as fact.
   *
   * BOUNDED, and the bound is REPORTED. `truncated` exists so the screen can say so — a cap nobody is told
   * about is indistinguishable from "that is all there is", which is the thing this fix exists to prevent.
   * Loop guards: stop if the cursor is missing or fails to advance, so a server that keeps saying `hasMore`
   * can never spin the client.
   */
  issues: async (): Promise<{ issues: import('./contract').Issue[]; truncated: boolean }> => {
    const MAX_PAGES = 10   // 250 issues; beyond that the screen says so rather than lying by omission
    const out: import('./contract').Issue[] = []
    let before: string | undefined
    let truncated = false
    for (let page = 0; page < MAX_PAGES; page++) {
      const payload = await call<any>('GET', `/issues${before ? `?before=${encodeURIComponent(before)}` : ''}`)
      out.push(...listOf<import('./contract').Issue>(payload, 'issues'))
      if (payload?.hasMore !== true) return { issues: out, truncated: false }
      const next = typeof payload.nextBefore === 'string' ? payload.nextBefore : undefined
      if (!next || next === before) return { issues: out, truncated: false }   // cursor stalled: stop, do not spin
      before = next
      truncated = true   // provisional: cleared by the `hasMore !== true` return above on the final page
    }
    return { issues: out, truncated }
  },
  issue: (issueId: string) => call<import('./contract').Issue>('GET', `/issues/${issueId}`),

  /**
   * Returns the thread, not just its messages. `locked` is DOMAIN, not transport: the server refuses a reply
   * to a closed thread with 409 `thread_locked`, and the portal used to discard the flag and gate the reply
   * box on `canComment` alone — a per-DESK setting that knows nothing about this thread. The customer typed a
   * message into a box that could not work and got "something went wrong on our side". Unwrapping the
   * envelope must not throw away a fact the screen needs.
   */
  comments: async (issueId: string): Promise<{ comments: import('./contract').IssueComment[]; locked: boolean }> => {
    const payload = await call<any>('GET', `/issues/${issueId}/comments`)
    return {
      comments: listOf<import('./contract').IssueComment>(payload, 'comments'),
      locked: payload?.locked === true,   // absent ⇒ not locked; only an explicit true closes the box
    }
  },
  addComment: (issueId: string, body: string) => call<import('./contract').IssueComment>('POST', `/issues/${issueId}/comments`, { body }),
  respondToInfoRequest: (issueId: string, body: string) => call<import('./contract').Issue>('POST', `/issues/${issueId}/info-response`, { body }),

  /**
   * ⚠️ SILENT BY DESIGN: this answers 200 with the ordinary issue projection, which carries no "approved"
   * field — so a success is indistinguishable from a no-op unless the caller says so itself. The screen
   * shows a session-local confirmation; it cannot survive a reload because there is nothing to read back.
   */
  approveResolution: (issueId: string) => call<import('./contract').Issue>('POST', `/issues/${issueId}/approve-resolution`),
  requestReopen: (issueId: string, reason: string) => call<import('./contract').Issue>('POST', `/issues/${issueId}/request-reopen`, { reason }),

  /* ── WORKSPACE SURFACES ────────────────────────────────────────────────────────────────
   *
   * ⚠️ EVERY ONE OF THESE USES THE `{items, count}` ENVELOPE — unlike the issue endpoints, which each use
   * their own key. That inconsistency is exactly what killed the first version of this portal, so the key is
   * named per call and `listOf` REFUSES a missing one rather than defaulting to an empty array. An empty
   * list and a broken contract must never render the same way.
   */
  workspaces: async (): Promise<import('./contract').Workspace[]> =>
    listOf<import('./contract').Workspace>(await call<any>('GET', '/workspaces'), 'items'),
  workspace: (id: string) => call<import('./contract').Workspace>('GET', `/workspaces/${id}`),

  sharedItems: async (wsId: string): Promise<import('./contract').SharedItem[]> =>
    listOf<import('./contract').SharedItem>(await call<any>('GET', `/workspaces/${wsId}/shared-items`), 'items'),
  filePackages: async (wsId: string): Promise<import('./contract').FilePackage[]> =>
    listOf<import('./contract').FilePackage>(await call<any>('GET', `/workspaces/${wsId}/file-packages`), 'items'),
  approvals: async (wsId: string): Promise<import('./contract').ApprovalRequest[]> =>
    listOf<import('./contract').ApprovalRequest>(await call<any>('GET', `/workspaces/${wsId}/approvals`), 'items'),
  forms: async (wsId: string): Promise<import('./contract').FormTemplate[]> =>
    listOf<import('./contract').FormTemplate>(await call<any>('GET', `/workspaces/${wsId}/forms`), 'items'),
  clientUpdates: async (wsId: string): Promise<import('./contract').ClientUpdate[]> =>
    listOf<import('./contract').ClientUpdate>(await call<any>('GET', `/workspaces/${wsId}/client-updates`), 'items'),

  /**
   * 🔴 A DOWNLOAD IS A WRITE. It records the access and mints a short-lived one-time URL, so it is a POST
   * and it must not be issued speculatively on render — only when the customer asks. The server may answer
   * `downloadAvailable: false`, which is a fact about the package, not a failure.
   */
  recordDownload: (packageId: string) =>
    call<import('./contract').DownloadTicket>('POST', `/file-packages/${packageId}/record-download`),

  /** `response` MUST be one of the approval's own `allowedResponses`, or the server answers response_not_allowed. */
  respondToApproval: (approvalId: string, response: string) =>
    call<import('./contract').WriteReceipt>('POST', `/external-approvals/${approvalId}/respond`, { response }),

  /**
   * Answers are keyed BY `fieldId`, which is why the screen must render from the template rather than from a
   * hand-written form: a field the server does not know is silently dropped by `buildAnswers`, and a required
   * one that is missing fails the submission as a whole.
   */
  submitForm: (formId: string, answers: Record<string, string>) =>
    call<import('./contract').WriteReceipt>('POST', `/forms/${formId}/submit`, { answers, idempotencyKey: newIdempotencyKey() }),

  /** The RFQ is addressed by its SHARED-ITEM id — the same row `/shared-items/:id` serves, under a different grant. */
  submitQuote: (sharedItemId: string, payload: { quoteTitle: string; totalAmount?: string; currency?: string; notes?: string }) =>
    call<import('./contract').WriteReceipt>('POST', `/supplier-rfqs/${sharedItemId}/quote-submissions`, { ...payload, idempotencyKey: newIdempotencyKey() }),

  acknowledgeUpdate: (shareId: string) =>
    call<import('./contract').WriteReceipt>('POST', `/client-updates/${shareId}/acknowledge`),

  logout: () => call<unknown>('POST', '/session/logout'),
}

export { call as __callForTests }
